<script>
import LabelDropdownItem from './LabelDropdownItem.vue';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import { picoSearch } from '@scmmishra/pico-search';
import { sanitizeLabel } from 'shared/helpers/sanitizeData';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    LabelDropdownItem,
    AddLabelModal,
    Hotkey,
    NextButton,
  },

  props: {
    accountLabels: {
      type: Array,
      default: () => [],
    },
    selectedLabels: {
      type: Array,
      default: () => [],
    },
    allowCreation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'add', 'remove', 'close'],

  data() {
    return {
      search: '',
      createModalVisible: false,
    };
  },

  computed: {
    createLabelPlaceholder() {
      const label = this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL');
      return this.search ? `${label}:` : label;
    },

    filteredActiveLabels() {
      if (!this.search) return this.accountLabels;

      const matches = picoSearch(this.accountLabels, this.search, ['title'], {
        threshold: 0.3,
      });

      return matches.slice(0, 8);
    },

    noResult() {
      return this.filteredActiveLabels.length === 0;
    },

    hasExactMatchInResults() {
      return this.filteredActiveLabels.some(
        label => label.title === this.search
      );
    },

    shouldShowCreate() {
      return (
        this.allowCreation &&
        this.search &&
        (this.search.length > 3 || this.filteredActiveLabels.length < 5)
      );
    },

    parsedSearch() {
      return sanitizeLabel(this.search);
    },
  },

  mounted() {
    this.focusInput();
  },

  methods: {
    focusInput() {
      this.$refs.searchbar.focus();
    },

    updateLabels(label) {
      this.$emit('update', label);
    },

    onAdd(label) {
      this.$emit('add', label);
    },

    onRemove(label) {
      this.$emit('remove', label);
    },

    onAddRemove(label) {
      if (this.selectedLabels.includes(label.title)) {
        this.onRemove(label.title);
      } else {
        this.onAdd(label);
      }
    },

    showCreateModal() {
      this.createModalVisible = true;
    },

    hideCreateModal() {
      this.createModalVisible = false;
    },

    closeModal() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <!-- Extended label modal overlay -->
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] mx-4"
      @click.stop
    >
      <!-- Modal header -->
      <div
        class="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700"
      >
        <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TITLE') }}
        </h2>
        <div class="flex items-center gap-2">
          <Hotkey
            custom-class="border border-solid text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-600 text-xs border-slate-75 dark:border-slate-600"
          >
            {{ 'L' }}
          </Hotkey>
          <button
            class="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            @click="closeModal"
          >
            <i class="i-lucide-x text-lg" />
          </button>
        </div>
      </div>

      <!-- Modal content -->
      <div class="flex flex-col p-4 min-h-[500px] max-h-[75vh]">
        <div class="mb-1">
          <input
            ref="searchbar"
            v-model="search"
            type="text"
            class="no-margin w-full px-4 py-1 text-base border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-100"
            autofocus
            :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
          />
        </div>

        <div class="flex-1 overflow-auto">
          <div
            v-if="filteredActiveLabels.length > 0"
            class="flex flex-col gap-1"
          >
            <LabelDropdownItem
              v-for="label in filteredActiveLabels"
              :key="label.title"
              :title="label.title"
              :color="label.color"
              :selected="selectedLabels.includes(label.title)"
              class="compact-label"
              @select-label="onAddRemove(label)"
            />
          </div>

          <div
            v-if="noResult"
            class="flex justify-center items-center py-12 text-slate-500 dark:text-slate-400"
          >
            {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
          </div>

          <div
            v-if="allowCreation && shouldShowCreate"
            class="mt-6 pt-4 border-t overflow-hidden border-slate-200 dark:border-slate-700"
          >
            <NextButton
              icon="i-lucide-plus"
              blue
              :label="`${createLabelPlaceholder} ${parsedSearch}`"
              :disabled="hasExactMatchInResults"
              class="w-full"
              @click="showCreateModal"
            />
            <woot-modal
              v-model:show="createModalVisible"
              @close="hideCreateModal"
            >
              <AddLabelModal
                :prefill-title="parsedSearch"
                @close="hideCreateModal"
              />
            </woot-modal>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
